import { useEffect, useState } from 'react';
import { Package, TrendingUp, TrendingDown, Wrench } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { formatJobNumber } from '../../../lib/docNumbers';

interface PartRow {
    id: string;
    description: string | null;
    // `cost` is already registration + date-aware (gross for a non-registered
    // / pre-registration purchase, net for a registered one) — the RPC decides.
    cost: number | string;
    category: string | null;
    source_item_type: string | null;
    header_date: string;
    vendor: string | null;
    job_number: number | null;
}

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

const formatUKDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return iso;
    }
};

export function PartsTrackerTab() {
    const { activeOrgId } = useVertical();
    const [parts, setParts] = useState<PartRow[]>([]);
    const [spend30, setSpend30] = useState(0);
    const [spendPrev30, setSpendPrev30] = useState(0);
    const [count30, setCount30] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false); // Collapse to the latest 10 by default

    useEffect(() => {
        if (!activeOrgId) return;

        const fetchParts = async () => {
            setLoading(true);

            const now = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(now.getDate() - 30);
            const sixtyDaysAgo = new Date();
            sixtyDaysAgo.setDate(now.getDate() - 60);

            // The RPC returns the last 60 days of parts purchases with a
            // registration + date-aware `cost` and the job's job_number. We keep
            // the pure date bucketing here (latest 30-day vs prior 30-day) for
            // the trend chip — no financial math in the client.
            const { data, error } = await supabase.rpc('fn_get_parts_purchases', {
                p_org_id: activeOrgId,
                p_days: 30,
            });

            if (error) {
                console.error('PartsTracker fetch failed', error);
                setLoading(false);
                return;
            }

            const rows = (data ?? []) as unknown as PartRow[];

            let s30 = 0, sPrev = 0, c30 = 0;
            for (const r of rows) {
                const d = new Date(r.header_date);
                const amount = Number(r.cost) || 0;
                if (d >= thirtyDaysAgo) { s30 += amount; c30 += 1; }
                else if (d >= sixtyDaysAgo) { sPrev += amount; }
            }

            setParts(rows.filter((r) => new Date(r.header_date) >= thirtyDaysAgo));
            setSpend30(s30);
            setSpendPrev30(sPrev);
            setCount30(c30);
            setLoading(false);
        };

        fetchParts();
    }, [activeOrgId]);

    const delta = spendPrev30 > 0 ? ((spend30 - spendPrev30) / spendPrev30) * 100 : null;
    const deltaUp = delta !== null && delta >= 0;

    const COLLAPSED_COUNT = 10;
    const isCollapsible = parts.length > COLLAPSED_COUNT;
    const visibleParts = isCollapsible && !showAll ? parts.slice(0, COLLAPSED_COUNT) : parts;

    return (
        <div className="space-y-6">
            {/* Headline stat */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1 relative rounded-2xl border border-gold/30 bg-raised backdrop-blur-xl shadow-lifted p-6 overflow-hidden">
                    <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
                    <div className="flex justify-between items-start">
                        <div className="flex flex-col gap-2">
                            <span className="text-fg-3 text-fd-sm-tight font-medium uppercase tracking-[0.2em] font-display">
                                30-day parts spend
                            </span>
                            <span className="text-3xl md:text-4xl font-bold font-mono tabular-nums text-fg tracking-tight">
                                {loading ? '—' : formatCurrency(spend30)}
                            </span>
                            <span className="text-fd-sm-tight text-fg-3 font-mono tabular-nums">
                                {count30} {count30 === 1 ? 'item' : 'items'} purchased
                            </span>
                        </div>
                        <div className="p-3 rounded-xl bg-gold-fill/10 border border-gold/20 text-gold">
                            <Package className="w-5 h-5" strokeWidth={2} />
                        </div>
                    </div>
                    {delta !== null && !loading && (
                        <div className="mt-4 inline-flex items-center gap-1.5 text-fd-sm-tight font-mono tabular-nums">
                            {deltaUp ? (
                                <TrendingUp className="w-3.5 h-3.5 text-danger" />
                            ) : (
                                <TrendingDown className="w-3.5 h-3.5 text-success" />
                            )}
                            <span className={deltaUp ? 'text-danger' : 'text-success'}>
                                {deltaUp ? '+' : ''}{delta.toFixed(0)}%
                            </span>
                            <span className="text-fg-3">vs prior 30 days</span>
                        </div>
                    )}
                </div>
            </div>

            {/* List panel */}
            <div className="rounded-2xl border border-line bg-raised backdrop-blur-xl shadow-card overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-line">
                    <div className="flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-gold" />
                        <h3 className="text-fd-sm-tight font-display font-semibold text-fg-3 uppercase tracking-[0.2em]">
                            Recent purchases
                        </h3>
                    </div>
                    <span className="text-fd-3xs font-mono tabular-nums text-fg-4">
                        Last 30 days
                    </span>
                </div>

                {loading ? (
                    <div className="px-5 py-12 text-center text-sm font-mono text-fg-4">—</div>
                ) : parts.length === 0 ? (
                    <div className="px-5 py-12 flex flex-col items-center gap-3 text-center">
                        <Package className="w-8 h-8 text-fg-4" strokeWidth={1.5} />
                        <p className="text-sm text-fg-2 max-w-sm">
                            No parts logged in the last 30 days. Upload a supplier receipt or tag a manual
                            expense with the <span className="text-gold">Parts</span> category.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-fd-3xs font-display font-semibold text-fg-3 uppercase tracking-[0.18em]">
                                <tr className="border-b border-line">
                                    <th className="px-5 py-3 text-left">Date</th>
                                    <th className="px-5 py-3 text-left">Item</th>
                                    <th className="px-5 py-3 text-left hidden md:table-cell">Supplier</th>
                                    <th className="px-5 py-3 text-left hidden lg:table-cell">Job</th>
                                    <th className="px-5 py-3 text-right">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleParts.map((p) => (
                                    <tr
                                        key={p.id}
                                        className="border-b border-line last:border-b-0 hover:bg-card transition-colors"
                                    >
                                        <td className="px-5 py-3 text-fg-3 font-mono tabular-nums text-xs">
                                            {formatUKDate(p.header_date)}
                                        </td>
                                        <td className="px-5 py-3 text-fg font-medium">
                                            {p.description || 'Unnamed part'}
                                        </td>
                                        <td className="px-5 py-3 text-fg-2 hidden md:table-cell">
                                            {p.vendor || '—'}
                                        </td>
                                        <td className="px-5 py-3 hidden lg:table-cell">
                                            {p.job_number != null ? (
                                                <span className="px-2 py-0.5 rounded-md bg-card border border-line font-mono text-fd-3xs text-fg-3 tabular-nums">
                                                    {formatJobNumber(p.job_number)}
                                                </span>
                                            ) : (
                                                <span className="text-fg-4 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono tabular-nums text-fg">
                                            {formatCurrency(Number(p.cost) || 0)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {isCollapsible && (
                            <div className="flex justify-center py-4 border-t border-line">
                                <button
                                    onClick={() => setShowAll((v) => !v)}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-fg-3 hover:text-gold bg-card hover:bg-raised border border-line transition-colors"
                                >
                                    {showAll ? 'Show less' : `Show all (${parts.length})`}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
